/**
 * The Log's attribute filters — interviewer, position, department (Sofia, Sep 10 2026).
 *
 * Split from InterviewsView for its 500-line ceiling. The dropdowns offer what the loaded rows
 * actually contain, each with its count, so an empty list can never be chosen; there is no separate
 * roster to read. '' means "any".
 */
import type { InterviewRow } from '../services/interviewsApi';

/** [value, count] pairs, sorted by value. */
export type FilterOption = [string, number];

const count = (values: (string | null | undefined)[]): FilterOption[] => {
  const m = new Map<string, number>();
  for (const v of values) if (v) m.set(v, (m.get(v) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
};

/** Interviewers are keyed by lowercased email (the display name is derived at render time). */
export function interviewFilterOptions(rows: InterviewRow[]) {
  return {
    interviewers: count(rows.flatMap((r) => r.interviewers.map((e) => e.toLowerCase()))),
    positions: count(rows.map((r) => r.jobTitle)),
    departments: count(rows.map((r) => r.department)),
  };
}

export interface AttributeFilters {
  interviewer: string;
  position: string;
  department: string;
}

/** Does the row pass the three attribute filters? Pure, shared with the tests. */
export function matchesAttributes(r: InterviewRow, f: AttributeFilters): boolean {
  if (f.interviewer && !r.interviewers.some((e) => e.toLowerCase() === f.interviewer)) return false;
  if (f.position && r.jobTitle !== f.position) return false;
  if (f.department && r.department !== f.department) return false;
  return true;
}

/** One attribute dropdown: "Any" plus the values the loaded rows carry, each with its count. */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** [value, display label] pairs, already sorted. */
  options: [string, string][];
}) {
  return (
    <label className="min-w-[170px]">
      <span className="mb-1 block text-xs font-medium text-gray-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Filter by ${label.toLowerCase()}`}
        className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700"
      >
        <option value="">Any</option>
        {options.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}
